
import React, { useState, useEffect } from 'react';

const SIZE = 10;
const BUGS_COUNT = 12;

type Cell = { isBug: boolean, isRevealed: boolean, isFlagged: boolean, neighborCount: number };

const Minesweeper: React.FC = () => {
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);

  const initGrid = () => {
    let newGrid: Cell[][] = Array(SIZE).fill(null).map(() => 
      Array(SIZE).fill(null).map(() => ({ isBug: false, isRevealed: false, isFlagged: false, neighborCount: 0 }))
    );

    let bugsPlaced = 0;
    while (bugsPlaced < BUGS_COUNT) {
      const r = Math.floor(Math.random() * SIZE);
      const c = Math.floor(Math.random() * SIZE);
      if (!newGrid[r][c].isBug) {
        newGrid[r][c].isBug = true;
        bugsPlaced++;
      }
    }

    for (let r = 0; r < SIZE; r++) {
      for (let c = 0; c < SIZE; c++) {
        if (!newGrid[r][c].isBug) {
          let count = 0;
          for (let i = -1; i <= 1; i++) {
            for (let j = -1; j <= 1; j++) {
              if (r+i >= 0 && r+i < SIZE && c+j >= 0 && c+j < SIZE && newGrid[r+i][c+j].isBug) count++;
            }
          }
          newGrid[r][c].neighborCount = count;
        }
      }
    }
    setGrid(newGrid);
    setGameOver(false);
    setWon(false);
  };

  useEffect(() => initGrid(), []);

  const reveal = (r: number, c: number) => {
    if (gameOver || won || grid[r][c].isRevealed || grid[r][c].isFlagged) return;

    const newGrid = [...grid.map(row => [...row])];
    if (newGrid[r][c].isBug) {
      setGameOver(true);
      newGrid.forEach(row => row.forEach(cell => cell.isBug && (cell.isRevealed = true)));
    } else {
      const floodFill = (row: number, col: number) => {
        if (row < 0 || row >= SIZE || col < 0 || col >= SIZE || newGrid[row][col].isRevealed || newGrid[row][col].isBug) return;
        newGrid[row][col].isRevealed = true;
        if (newGrid[row][col].neighborCount === 0) {
          for (let i = -1; i <= 1; i++) {
            for (let j = -1; j <= 1; j++) floodFill(row + i, col + j);
          }
        }
      };
      floodFill(r, c);
    }

    setGrid(newGrid);
    checkWin(newGrid);
  };

  const checkWin = (g: Cell[][]) => {
    const revealedCount = g.flat().filter(c => c.isRevealed).length;
    if (revealedCount === (SIZE * SIZE - BUGS_COUNT)) setWon(true);
  };

  return (
    <div className="flex flex-col items-center">
      <div className="mb-6 flex gap-4 items-center">
        <h2 className={`text-2xl font-bold ${gameOver ? 'text-red-500' : won ? 'text-green-500' : 'text-slate-300'}`}>
          {gameOver ? "CRASHED!" : won ? "DEPLOYED!" : "Find the Bugs"}
        </h2>
        <button onClick={initGrid} className="bg-slate-700 px-4 py-1 rounded text-sm hover:bg-slate-600 transition-colors">Reset</button>
      </div>
      <div className="grid grid-cols-10 gap-1 bg-slate-950 p-2 rounded-lg border border-slate-800">
        {grid.map((row, r) => row.map((cell, c) => (
          <button
            key={`${r}-${c}`}
            onClick={() => reveal(r, c)}
            onContextMenu={(e) => { e.preventDefault(); /* Flagging omitted for simplicity */ }}
            className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center font-bold text-sm transition-all ${
              cell.isRevealed 
                ? cell.isBug ? 'bg-red-500' : 'bg-slate-800 text-slate-400' 
                : 'bg-slate-700 hover:bg-slate-600'
            } rounded-sm`}
          >
            {cell.isRevealed && !cell.isBug && cell.neighborCount > 0 ? cell.neighborCount : cell.isRevealed && cell.isBug ? '💣' : ''}
          </button>
        )))}
      </div>
    </div>
  );
};

export default Minesweeper;
